import {
  useState,
  useCallback,
  type ReactElement,
  type ReactNode,
} from "react";
import parseHTMLToReact, {
  type HTMLReactParserOptions,
} from "html-react-parser";
import { marked } from "marked";
import { Icon, Flex } from "orcs-design-system";
import {
  GradientBorder,
  Content,
  ToggleButton,
  CollapsibleContent,
  TypingIndicator,
} from "./StyledComponents";
import { AIWarning } from "./AIWarning";
import AIIcon from "./AI_Icon";
import { DASHBOARD_REGEX_V2, E_CHART_REGEX, GRAPH_REGEX } from "./consts";
import { replaceTagWhileStreaming } from "./utils";
import { coerceToStreamText } from "./utils/extractStreamText";

const parseMDToHTML = (text: string): string => {
  const html = marked.parse(text, { async: false });
  return typeof html === "string" ? html : "";
};

const renderAIMessageContent = (
  content: string,
  options: HTMLReactParserOptions | undefined,
  complete: boolean
): ReactNode => {
  let outText = content;
  try {
    outText = replaceTagWhileStreaming(outText, GRAPH_REGEX, "graph", complete);
    outText = replaceTagWhileStreaming(
      outText,
      DASHBOARD_REGEX_V2,
      "dashboard",
      complete
    );
    outText = replaceTagWhileStreaming(
      outText,
      E_CHART_REGEX,
      "e-chart",
      complete
    );
    return parseHTMLToReact(parseMDToHTML(outText), options);
  } catch {
    return <span>{content}</span>;
  }
};

export type AIMessageCollapsible = "collapsed" | "expanded";

export type AIMessageProps = {
  content?: string;
  options?: HTMLReactParserOptions;
  complete?: boolean;
  showAIWarning?: boolean;
  /** omitted = no toggle; "expanded" / "collapsed" = starts in that state */
  collapsible?: AIMessageCollapsible;
  onReveal?: () => void;
  title?: string;
  ariaLabel?: string;
  errorMessage?: ReactNode;
};

export const AIMessage = ({
  content = "",
  options,
  complete = false,
  showAIWarning = true,
  collapsible,
  onReveal = () => {},
  title = "AI Summary",
  ariaLabel = "AI Message",
  errorMessage,
}: AIMessageProps): ReactElement | null => {
  const [isCollapsed, setIsCollapsed] = useState(collapsible === "collapsed");
  const textContent = coerceToStreamText(content);

  const toggleCollapsed = useCallback(() => {
    setIsCollapsed((prev) => {
      if (prev && !textContent) {
        onReveal();
      }
      return !prev;
    });
  }, [onReveal, textContent]);

  if (!(textContent.length > 0 || collapsible || errorMessage)) {
    return null;
  }

  return (
    <GradientBorder>
      <Content as="section" isCollapsed={isCollapsed} aria-label={ariaLabel}>
        {collapsible ? (
          <ToggleButton
            onClick={toggleCollapsed}
            isCollapsed={isCollapsed}
            aria-label={isCollapsed ? `Expand ${title}` : `Collapse ${title}`}
          >
            <Flex alignItems="center" gap="s">
              {title ? <AIIcon width={20} height={19} /> : null}
              {title}
            </Flex>
            <Icon
              icon={
                isCollapsed ? ["fas", "chevron-down"] : ["fas", "chevron-up"]
              }
              size="xs"
            />
          </ToggleButton>
        ) : null}
        <CollapsibleContent
          {...(collapsible ? { collapsible } : {})}
          isCollapsed={isCollapsed}
        >
          {errorMessage ? (
            errorMessage
          ) : (
            <>
              {renderAIMessageContent(textContent, options, complete)}
              {complete ? null : <TypingIndicator />}
            </>
          )}
          {!errorMessage && (showAIWarning || (complete && textContent)) ? (
            <Flex justifyContent="space-between">
              {showAIWarning ? <AIWarning /> : null}
            </Flex>
          ) : null}
        </CollapsibleContent>
      </Content>
    </GradientBorder>
  );
};
